import { Badge, Button, Col, Icon, Popover, Row, Select, Spin, Table, Tabs, Tag } from 'antd';
import colors from 'distinct-colors';
import FileDownload from 'js-file-download';
import moment from 'moment';
import React from 'react';
import { connect } from 'react-redux';
import { SortableContainer, SortableElement } from 'react-sortable-hoc';
import Swal from 'sweetalert2';
import { TitledContainer } from '../../../components/TitledContainer/TitledContainer';
import { applyDelay } from '../../../core-module/features/route-delays/applyDelay';
import { delayCalculator } from '../../../core-module/features/route-delays/delayCalculator';
import { formatFullDriverName } from '../../../core-module/helpers/nameFormatting';
import { hasRole, RoleTypes } from '../../../core-module/helpers/RoleHelper';
import { metersToKilometers, minutesToSeconds } from '../../../core-module/helpers/RouteHelper';
import { loopBackApi } from '../../../core-module/models/LoopBack/Api/LoopBackApi';
import LoopBackEndpoint from '../../../core-module/models/LoopBack/Models/LoopBackEndpoint';
import RoutesEndpoint from '../../../core-module/models/LoopBack/Models/RoutesEndpoint';
import { DefaultAuthState } from '../../../core-module/models/redux/DefaultAuthState';
import { defaultRouteState } from '../../../core-module/models/redux/DefaultRouteState';
import {
  fetchActualDepotRoutes,
  fetchRouteTemplates,
  swapIndexes
} from '../../../core-module/redux/Route/actions';
import { TableState } from '../../../core-module/redux/Table/reducer';
import { isBevoorradingsRoute } from '../../../helpers/RouteTemplateHelper';
import TableColumn from '../../../models/Table/TableColumn';
import CrudForm from '../../CrudForms/CrudForm';
import RouteForm from '../../CrudForms/RouteForm';
import RouteMap from '../../Map/RouteMap';
import RouteMapAlt from '../../Map/RouteMapAlt';
import TableWithModal from '../../Table/TableWithModal';

L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png')
});

const { TabPane } = Tabs;
const SortableItem = SortableElement(({ value }) => <li>{value}</li>);

const SortableList = SortableContainer(({ items }) => {
  return (
    <ul className={'sortable-list'}>
      {items
        .sort((a, b) => a.route_order - b.route_order)
        .map((data, index) => (
          <SortableItem
            key={`item-${index}`}
            index={index}
            value={
              <span>
                <Badge count={index + 1} style={{ backgroundColor: 'dodgerblue' }} />
                &nbsp;&nbsp;
                {data.customer.first_name} {data.customer.prefix || ''} {data.customer.last_name}
                &nbsp;| {data.customer.street_name + ' ' + data.customer.door_number}
                &nbsp;| {data.customer.postal_code + ' ' + data.customer.city}
              </span>
            }
          />
        ))}
    </ul>
  );
});

interface DriverDashboardProps {
  routes: typeof defaultRouteState;
  auth: DefaultAuthState;
  table: TableState;
}

interface DispatchProps {
  fetchRouteTemplates: typeof fetchRouteTemplates;
  fetchActualDepotRoutes: typeof fetchActualDepotRoutes;
  swapIndexes: typeof swapIndexes;
}

interface State {
  date: string;
  templates: any[];
  init: boolean;
  tabIndex: string;
  selectedRouteIndex: any;
  isDepotAdmin: boolean;
  saving: boolean;
}

type Props = DriverDashboardProps & DispatchProps;

class Routes extends React.Component<Props, State> {
  private readonly loopBackEndpoint: LoopBackEndpoint;
  private readonly columns: TableColumn[];

  constructor(props) {
    super(props);
    this.loopBackEndpoint = new RoutesEndpoint();

    let isDepotAdmin = hasRole(this.props.auth.roles, RoleTypes.DEPOT_ADMIN);
    let isDistributorAdmin = hasRole(this.props.auth.roles, RoleTypes.DISTRIBUTOR_ADMIN);

    this.state = {
      date: '',
      templates: [],
      init: false,
      saving: false,
      tabIndex: '1',
      selectedRouteIndex: isDepotAdmin || isDistributorAdmin ? 'all' : null,
      isDepotAdmin: isDepotAdmin || isDistributorAdmin
    };

    this.columns = [
      new TableColumn('Naam', 'name', 'name'),
      new TableColumn('Datum', 'actual_date_driven', 'actual_date_driven').setRender((rowData) => {
        return moment(rowData).format('DD MMM');
      }),
      new TableColumn('Levermoment', 'delivery_moment.name', 'delivery_moment'),
      new TableColumn('Adressen', '', 'orders').setRender((rowData) => {
        return rowData.orders ? rowData.orders.length : '-';
      }),
      new TableColumn('Gereden km', '', 'actual_driven_km').setRender((rowData) => {
        if (!rowData.actual_driven_km) {
          return {
            props: { className: 'bg-alert' },
            children: (
              <span>
                <em style={{ color: 'white' }}>Nog niet ingevuld</em>
              </span>
            )
          };
        }
        return rowData.actual_driven_km;
      })
    ];

    if (isDistributorAdmin === false) {
      this.columns.push(new TableColumn('Temperatuur', 'temperature', 'temperature'));
    }

    this.columns.push(
      new TableColumn('Acties', '', 'actions')
        .setAdditional({ width: '100px' })
        .setRender((rowData) => {
          const now = moment();
          const currentMonth = now.month();
          const currentYear = now.year();

          const dateDrivenMoment = moment(rowData.actual_date_driven);
          const dateDrivenMonth = dateDrivenMoment.month();
          const dateDrivenYear = dateDrivenMoment.year();

          if (dateDrivenMonth >= currentMonth && dateDrivenYear >= currentYear) {
            return (
              <CrudForm
                title='Route bewerken'
                showButtonText={false}
                crudForm={
                  <RouteForm
                    stateKey='routes'
                    rowData={rowData}
                    type='edit'
                    loopBackEndpoint={this.loopBackEndpoint}
                  />
                }
              />
            );
          }

          return <></>;
        })
    );

    this.onRouteChange = this.onRouteChange.bind(this);
    this.exportRouteList = this.exportRouteList.bind(this);
  }

  getColorString(palette, index) {
    if (palette && palette.length - 1 >= index) {
      let color = palette[index]._rgb;
      return `rgb(${color[0]},${color[1]},${color[2]})`;
    }
    return '#aaa';
  }

  componentDidMount() {
    document.title = 'Logistics | Routes';
    const userId = this.props.auth.user.driver!.id;
    if (userId === undefined) {
      return;
    }

    this.props.fetchRouteTemplates(userId, {
      filter: { include: { route_customers: ['customer'] } }
    });
    this.props.fetchActualDepotRoutes();
  }

  private stripeTable = () => 'striped-table-row';

  saveRouteOrder = async (templateIndex: number) => {
    let template = this.state.templates[templateIndex];
    let routeCustomers = template.route_customers.sort((a, b) => a.route_order - b.route_order);
    this.setState({ saving: true });
    let error = false;
    for (let index in routeCustomers) {
      if (routeCustomers.hasOwnProperty(index)) {
        let routeCustomer = routeCustomers[index];
        try {
          await loopBackApi().patch(`route-customers/by-user/${routeCustomer.id}`, {
            route_order: parseInt(index)
          });
        } catch (err) {
          error = true;
        }
      }
    }
    this.setState({ saving: false });
    if (error === false) {
      Swal({
        title: 'Volgorde opgeslagen',
        text: `De route volgorde is succesvol opgeslagen`,
        type: 'success',
        confirmButtonText: 'Ok'
      }).then((result) => {});
    } else {
      Swal({
        title: 'Helaas',
        text: `Er is iets mis gegaan bij het opslaan van de route volgorde. Probeer het later nogmaals of neem contact op`,
        type: 'error',
        confirmButtonText: 'Ok'
      }).then((result) => {});
    }
  };

  componentWillReceiveProps(nextProps: Props) {
    this.setState({ templates: nextProps.routes.templates });
  }

  getOrdersTableColumns(route) {
    const isSupplyRoute = isBevoorradingsRoute(route.route_template);

    let columns = [
      new TableColumn('#', 'route_order', 'route_order')
        .setAdditional({ width: '50px' })
        .setRender((rowData) => rowData + 1),
      new TableColumn('Klant', '', 'customer').setRender((rowData) => {
        if (isSupplyRoute) {
          return `Depot ${rowData.depot.name}`;
        }

        return (
          rowData.customer.first_name +
          ' ' +
          (rowData.customer.prefix
            ? rowData.customer.prefix + ' ' + rowData.customer.last_name
            : rowData.customer.last_name)
        );
      }),
      new TableColumn('Adres', '', 'address').setRender((rowData) => (
        <div style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
          <div style={{ whiteSpace: 'nowrap' }}>
            {rowData.street_name} {rowData.door_number}
          </div>
          <div style={{ whiteSpace: 'nowrap' }}>
            {rowData.postal_code} {rowData.city}
          </div>
        </div>
      )),
      new TableColumn('Afstand x->y', '', 'distance_result_array').setRender((rowData) => {
        if (route.distance_result_array) {
          let meters = route.distance_result_array[rowData.route_order + 1];
          // add depot distance to first item in the array because depot is not show on the map.
          if (rowData.route_order === 0) {
            // depot distance
            meters += route.distance_result_array[0];
          }

          return `${metersToKilometers(parseInt(meters))} km`;
        } else {
          return '-';
        }
      }),
      new TableColumn('Verstreken Afstand', '', 'distance').setRender((rowData) => {
        if (route.distance_result_array) {
          const spliced = route.distance_result_array.slice().splice(0, rowData.route_order + 2);
          const meters = spliced.reduce((total, num) => total + parseInt(num), 0);

          return `${metersToKilometers(parseInt(meters))} km`;
        } else {
          return '-';
        }
      }),
      new TableColumn('Reistijd x->y', '', 'travel_time').setRender((rowData) => {
        if (route.time_result_array) {
          let seconds = route.time_result_array[rowData.route_order + 1];

          // add depot time to first item in the array because depot is not show on the map.
          if (
            route.depot &&
            !route.depot.include_time_to_first_arrival &&
            rowData.route_order === 0
          ) {
            // depot time
            seconds += route.time_result_array[0];
          }

          // add waiting time to all other items on the list except the first (no waiting time yet)
          if (rowData.route_order !== 0) {
            // waiting time is in minutes
            seconds += minutesToSeconds(route.waiting_time);
          }
          return this.secondsToHmsShort(seconds);
        } else {
          return '-';
        }
      }),
      new TableColumn('Aankomst', '', 'delivery_time_exact').setRender((rowData) => {
        const delayInMinutes = delayCalculator(rowData.delivery_time_exact, route.delays);
        return (
          <span>
            {rowData.delivery_time_early} - {rowData.delivery_time_late}
            {delayInMinutes > 0 && (
              <p style={{ color: 'red', fontWeight: 600 }}>
                {applyDelay(rowData.delivery_time_early, delayInMinutes)} -{' '}
                {applyDelay(rowData.delivery_time_late, delayInMinutes)}
              </p>
            )}
          </span>
        );
      }),
      new TableColumn('Status', 'status', 'status')
        .setAdditional({ width: 120 })
        .setRender((status, rowData, index) => {
          const statusses = {
            delivered: 'Afgeleverd',
            shipping: 'Te leveren',
            imported: 'Verwerken'
          };
          const delivered = rowData.delivered_date
            ? moment(rowData.delivered_date).format('HH:mm')
            : undefined;
          return {
            props: {
              style: {
                backgroundColor: status === 'delivered' ? '#5BBA5B' : 'orange',
                textAlign: 'center'
              }
            },
            children: rowData.status_description ? (
              <Popover content={<span>{rowData.status_description}</span>} title='Beschrijving'>
                {delivered ? (
                  <div style={{ margin: '0 auto', whiteSpace: 'nowrap' }}>
                    <div style={{ color: 'white', display: 'block' }}>
                      <span style={{ textDecoration: 'underline dotted' }}>
                        {statusses[status]}
                      </span>{' '}
                      <Icon type={'info-circle-o'} />
                    </div>
                    <span style={{ color: 'white', display: 'block' }}>{delivered}</span>
                  </div>
                ) : (
                  <span style={{ textDecoration: 'underline dotted', color: 'white' }}>
                    {statusses[status]} <Icon type={'info-circle-o'} />
                  </span>
                )}
              </Popover>
            ) : delivered ? (
              <div style={{ margin: '0 auto', whiteSpace: 'nowrap' }}>
                <span style={{ color: 'white', display: 'block' }}>{statusses[status]}</span>
                <span style={{ color: 'white', display: 'block' }}>{delivered}</span>
              </div>
            ) : (
              <span style={{ color: 'white' }}>{statusses[status]}</span>
            )
          };
        })
    ];

    if (isBevoorradingsRoute(route.route_template)) {
      columns.push(
        new TableColumn('Dozen', '', 'num_of_boxes').setRender(
          (rowData) => rowData.num_of_boxes || ''
        )
      );
      columns.push(
        new TableColumn('Tassen', '', 'num_of_bags').setRender(
          (rowData) => rowData.num_of_bags || ''
        )
      );
      columns.push(
        new TableColumn('Containers', '', 'num_of_containers').setRender(
          (rowData) => rowData.num_of_containers || ''
        )
      );
      columns.push(
          new TableColumn('Kratten', '', 'num_of_crates').setRender(
              (rowData) => rowData.num_of_crates || ''
          )
      );
    }

    const isDistributorAdmin = hasRole(this.props.auth.roles, RoleTypes.DISTRIBUTOR_ADMIN);
    if (isDistributorAdmin) {
      columns.push(new TableColumn('Temperatuur', 'temperature', 'temperature'));
    }

    return columns;
  }

  pad(d) {
    return d < 10 ? '0' + d.toString() : d.toString();
  }

  setDate = (date) => {
    this.setState({ date: date });
  };

  getDateRoutes = (date, all = false) => {
    let dateRoutes = this.props.routes.actualRoutes.filter((route) => {
      return this.getActualDateDriven(route) === moment(date).format('YYYY-MM-DD');
    });

    return dateRoutes.filter(
      (route) =>
        route.driver_id === this.props.auth.user.driver!.id ||
        all ||
        hasRole(this.props.auth.roles, RoleTypes.DEPOT_ADMIN) ||
        hasRole(this.props.auth.roles, RoleTypes.DISTRIBUTOR_ADMIN)
    );
  };

  secondsToHmsShort(d) {
    d = Number(d);
    let h = Math.floor(d / 3600);
    let m = Math.floor((d % 3600) / 60);
    let hDisplay = h > 0 ? this.pad(h) : '00';
    let mDisplay = m > 0 ? this.pad(m) : '00';
    return hDisplay + ':' + mDisplay;
  }

  onSortEnd = (oldIndex: number, newIndex: number, templateIndex: number) => {
    this.props.swapIndexes(oldIndex, newIndex, templateIndex);
  };

  onRouteChange(e) {
    this.setState({ selectedRouteIndex: e });
  }

  exportRouteList() {
    let date = this.state.date;

    if (!date || date === '') {
      date = this.getFirstApplicableDateForDriver(
        this.props.routes.actualRoutes,
        this.props.auth.user.driver
      );
      if (!!date) {
        this.setDate(date);
      }
    }

    let dateRoutes = this.getDateRoutes(date);
    let route = dateRoutes[this.state.selectedRouteIndex ? this.state.selectedRouteIndex : 0];

    loopBackApi()
      .download(`routes/export-by-user/${route.id}`)
      .then((res) =>
        FileDownload(
          res.data,
          `Route Lijst ${route.name} | ${moment(route.actual_date_driven).format('DD MMM')}.xlsx`
        )
      )
      .catch((err) => ({}));
  }

  getFirstApplicableDateForDriver(dates, driver) {
    const allowedRoutesForDriver = dates.filter((_route) => _route.driver_id === driver.id);
    const filtered = allowedRoutesForDriver.map((_route) => {
      return this.getActualDateDriven(_route);
    });

    return filtered[0] || null;
  }

  getMyActualRoute = (style) => {
    let dates: string[] = this.props.routes.actualRoutes.map((_route) => {
      return this.getActualDateDriven(_route);
    });

    if (this.blockFutureRoutes()) {
      dates = dates.filter((date) => moment().diff(date, 'days') > 0);
    }

    dates = Array.from(new Set(dates));

    let date = this.state.date;
    if (!date || date === '') {
      date = this.getFirstApplicableDateForDriver(
        this.props.routes.actualRoutes,
        this.props.auth.user.driver
      );
      if (!!date) {
        this.setDate(date);
      }
    }

    let dateRoutes = this.getDateRoutes(date);
    let route = dateRoutes[this.state.selectedRouteIndex ? this.state.selectedRouteIndex : 0];

    return (
      <div>
        {this.selector(style, dateRoutes, dates)}
        <Row>
          <Col xs={24}>
            {route ? (
              <RouteMap routes={[route]} height={500} width={800} />
            ) : (
              <div>Kaart laden...</div>
            )}
          </Col>
        </Row>
        {route && (
          <Row>
            <Col xs={24} style={style}>
              <Row>
                <Col xs={6}>
                  <b>Naam:</b> {route.name}
                </Col>
                <Col xs={6}>
                  <span>
                    <b>Adressen:</b> {route.orders.length}
                  </span>
                  &nbsp;&nbsp;
                </Col>
                <Col xs={6}>
                  <b>Gereden km: </b>
                  {route.actual_driven_km !== null ? (
                    route.actual_driven_km
                  ) : (
                    <i>Nog niet doorgegeven..</i>
                  )}
                </Col>
                <Col xs={6}>
                  <b>Notitie: </b>
                  {route.comments !== null ? route.comments : <i>Geen</i>}
                </Col>
              </Row>
            </Col>
          </Row>
        )}
        {route && (
          <Row>
            <Table size={'small'} {...this.getOrdersTableProps(route)} />
          </Row>
        )}
      </div>
    );
  };

  getActualDateDriven = (route) => {
    let dateDrivenMoment = moment(route.actual_date_driven);
    return dateDrivenMoment.format('YYYY-MM-DD');
  };

  selector = (style, dateRoutes, dates) => {
    if (
      hasRole(this.props.auth.roles, RoleTypes.DEPOT_ADMIN) ||
      hasRole(this.props.auth.roles, RoleTypes.DISTRIBUTOR_ADMIN)
    ) {
      dates = dates.filter((_date) =>
        this.props.routes.actualRoutes.some((route) => {
          return this.getActualDateDriven(route) === _date;
        })
      );
    } else {
      dates = dates.filter((_date) =>
        this.props.routes.actualRoutes.some((route) => {
          return (
            this.getActualDateDriven(route) === _date &&
            this.props.auth.user.driver!.id === route.driver_id
          );
        })
      );
    }

    return (
      <Row>
        <Col xs={24} style={style}>
          <Row>
            <Col xs={24} sm={6}>
              Datum:
              <Select
                showSearch={true}
                style={{ width: 200, marginLeft: 5, marginBottom: 5 }}
                placeholder={'Selecteer een datum'}
                onChange={this.setDate}
                defaultValue={dates[0]}
              >
                {dates.map((_date, index) => {
                  return (
                    <Select.Option key={index.toString()} value={_date}>
                      {moment(_date).format('D MMM YYYY')}
                    </Select.Option>
                  );
                })}
              </Select>
            </Col>

            <Col xs={24} sm={10}>
              Route:
              <Select
                showSearch={true}
                style={{ width: 240, marginLeft: 5 }}
                placeholder={'Selecteer een route'}
                onChange={this.onRouteChange}
                defaultValue={this.state.selectedRouteIndex ? this.state.selectedRouteIndex : 0}
              >
                {this.state.isDepotAdmin ? (
                  <Select.Option key={'all'} value={'all'}>
                    Alle routes samen
                  </Select.Option>
                ) : undefined}
                {dateRoutes.map((r, index) => (
                  <Select.Option key={index.toString()} value={index}>
                    {r.name} | {r.delivery_moment && r.delivery_moment.name}
                  </Select.Option>
                ))}
                {!this.state.isDepotAdmin ? (
                  <Select.Option key={'all'} value={'all'}>
                    Alle routes samen
                  </Select.Option>
                ) : undefined}
              </Select>
            </Col>

            {this.state.selectedRouteIndex !== 'all' && this.props.routes.actualRoutes.length > 0 && (
              <Col xs={24} sm={{ offset: 4, span: 4 }}>
                <Button onClick={this.exportRouteList} type={'primary'}>
                  Download routelijst
                </Button>
              </Col>
            )}
          </Row>
        </Col>
      </Row>
    );
  };

  blockFutureRoutes() {
    let currentWeekDay = moment().isoWeekday();
    let currentHour = moment().hour();

    return (
      (currentWeekDay === 7 && currentHour > 22) ||
      (currentWeekDay === 1 && currentHour < 10) ||
      (currentWeekDay === 3 && currentHour > 22) ||
      (currentWeekDay === 4 && currentHour < 10)
    );
  }

  getOverviewRoutes = (style) => {
    let dates: string[] = this.props.routes.actualRoutes.map((_route) => {
      return this.getActualDateDriven(_route);
    });

    if (this.blockFutureRoutes()) {
      dates = dates.filter((date) => moment().diff(date, 'days') > 0);
    }

    dates = Array.from(new Set(dates));

    let date = this.state.date;
    if (!date || date === '') {
      date = dates[0];
      this.setDate(date);
    }

    let dateRoutesChoices = this.getDateRoutes(date, false);
    let dateRoutes = this.getDateRoutes(date, true);

    return (
      <div>
        {this.selector(style, dateRoutesChoices, dates)}
        <Row>
          <Col xs={24}>
            {dateRoutes.length > 0 ? (
              <RouteMap routes={dateRoutes} height={500} width={800} />
            ) : (
              <div>Kaart laden...</div>
            )}
          </Col>
        </Row>
        <Row>
          <Col xs={24} style={style}></Col>
        </Row>
        <Row>
          <Table size={'small'} {...this.getOverviewTableProps(dateRoutes)} />
        </Row>
      </div>
    );
  };

  getOverviewTableProps(routes) {
    let palette = colors({ count: routes.length, lightMin: 10, hueMin: 10, chromaMin: 70 });

    let overviewColumns = [
      new TableColumn('Index', 'route_index', 'route_index').setRender((rowData, index) => {
        if (palette) {
          return {
            props: { style: { backgroundColor: this.getColorString(palette, rowData) } }
          };
        }
        return '-';
      }),
      new TableColumn('Naam', 'name', 'name'),
      new TableColumn('Bezorger', '', 'driver').setRender((rowData) => {
        const name = formatFullDriverName(rowData.driver);

        if (rowData.driver_id !== rowData.original_driver_id) {
          // Other driver than on the route template
          return (
            <span>
              {name} <Tag color='#f50'>TIJDELIJK</Tag>
            </span>
          );
        } else {
          // Default driver
          return name;
        }
      }),
      new TableColumn('Afstand (km)', 'total_km', 'total_km'),
      new TableColumn('Tijd (HH:mm)', 'total_nett_route_time', 'total_nett_route_time').setRender(
        (rowData) => {
          return this.secondsToHmsShort(rowData);
        }
      ),
      new TableColumn('Bestellingen', 'num_of_orders', 'num_of_orders')
    ];

    if (routes.some((x) => isBevoorradingsRoute(x.route_template))) {
      overviewColumns.push(
        new TableColumn('Dozen', '', 'num_of_boxes').setRender(
          (rowData) => rowData.num_of_boxes || ''
        )
      );
      overviewColumns.push(
        new TableColumn('Tassen', '', 'num_of_bags').setRender(
          (rowData) => rowData.num_of_bags || ''
        )
      );
      overviewColumns.push(
        new TableColumn('Containers', '', 'num_of_containers').setRender(
          (rowData) => rowData.num_of_containers || ''
        )
      );
      overviewColumns.push(
        new TableColumn('Kratten', '', 'num_of_crates').setRender(
          (rowData) => rowData.num_of_crates || ''
        )
      );
    }

    const ordersColumns = overviewColumns.map((column: TableColumn) => column.get());

    return {
      bordered: true,
      loading: false,
      columns: ordersColumns,
      rowKey: 'id',
      dataSource: routes,
      rowClassName: this.stripeTable,
      locale: { emptyText: 'Geen data beschikbaar' },
      pagination: { pageSize: 999, hideOnSinglePage: true }
    };
  }

  getOrdersTableProps(route) {
    const ordersColumns = this.getOrdersTableColumns(route).map((column: TableColumn) =>
      column.get()
    );

    return {
      bordered: true,
      loading: false,
      columns: ordersColumns,
      rowKey: 'id',
      dataSource: route.orders.sort((a, b) => a.route_order - b.route_order),
      rowClassName: this.stripeTable,
      locale: { emptyText: 'Geen data beschikbaar' },
      pagination: { pageSize: 999, hideOnSinglePage: true }
    };
  }

  getRoutes() {
    const style = {
      backgroundColor: '#777',
      minHeight: '65px',
      padding: '15px',
      color: '#fff',
      verticalAlign: 'center'
    };

    if (this.state.selectedRouteIndex !== 'all') {
      return this.getMyActualRoute(style);
    } else {
      return this.getOverviewRoutes(style);
    }
  }

  render() {
    const columns = this.columns.map((column: TableColumn) => column.get());
    const userId = this.props.auth.user.driver!.id;

    const filter = {
      filter: {
        fields: [
          'id',
          'actual_date_driven',
          'original_driver_id',
          'driver_id',
          'name',
          'delivery_moment_id',
          'actual_driven_km',
          'comments',
          'temperature'
        ],
        include: [
          {
            relation: 'orders',
            scope: {
              fields: ['id']
            }
          },
          {
            relation: 'delivery_moment',
            scope: {
              fields: ['id', 'name']
            }
          }
        ],
        where: {
          and: [{ driver_id: userId }]
        },
        order: 'actual_date_driven DESC',
        limit: 10
      }
    };

    let count =
      this.props.table.tables.routes && this.props.table.tables.routes.tableData
        ? this.props.table.tables.routes.tableData.reduce(
            (a, b) => (b.actual_driven_km ? 0 : 1) + a,
            0
          )
        : 0;

    return (
      <TitledContainer title={'Route'}>
        <Tabs defaultActiveKey='1' animated={false}>
          <TabPane tab={'Actuele Routes'} key={'1'}>
            {this.props.routes.loadingActualRoutes ? (
              <span>
                <Spin spinning={true} /> <em>Recente routes aan het laden...</em>
              </span>
            ) : (
              this.getRoutes()
            )}
          </TabPane>
          <TabPane
            forceRender={true}
            tab={
              count ? (
                <Badge offset={[7, -5]} count={count}>
                  Gereden routes
                </Badge>
              ) : (
                'Gereden routes'
              )
            }
            key={'2'}
          >
            <Row>
              <Col xs={24}>
                <TableWithModal
                  rowKey={(row, index) => index}
                  title={'Gereden Routes'}
                  bordered={true}
                  striped={true}
                  tableOnly={true}
                  searchFields={['name']}
                  endPoint={'routes/by-user'}
                  loopBackEndpoint={this.loopBackEndpoint}
                  stateKey={'routes'}
                  columns={columns}
                  pagination={{ pageSize: 999, hideOnSinglePage: true }}
                  filters={filter}
                  component={
                    <span style={{ color: '#aaa' }}>
                      <em>Onderstaande tabel toont alleen de laatste 15 gereden routes</em>
                    </span>
                  }
                />
              </Col>
            </Row>
          </TabPane>
          {this.state.templates.length > 0 && (
            <TabPane tab={'Route volgorde'} key={'3'}>
              <em style={{ color: '#aaa' }}>
                Klik op een klant in de lijst hieronder en versleep om de volgorde van de route aan
                te passen.
              </em>
              <br />
              <em style={{ color: '#aaa' }}>
                Klik vervolgens op Routevolgorde opslaan om te nieuwe volgorde op te slaan.
              </em>
              <br />
              <br />
              <Tabs defaultActiveKey={'0'} animated={false} type={'card'}>
                {this.state.templates.map((routeTemplate, templateIndex) => {
                  if (routeTemplate.has_custom_route_order) {
                    return (
                      <TabPane
                        tab={
                          routeTemplate.name +
                          ' (' +
                          moment().isoWeekday(routeTemplate.day_of_week).format('dddd') +
                          ')'
                        }
                        key={templateIndex.toString()}
                      >
                        <Row key={templateIndex} gutter={12}>
                          <Col xs={24} sm={12}>
                            <Button
                              onClick={() => this.saveRouteOrder(templateIndex)}
                              type={'primary'}
                              style={{ marginBottom: 10 }}
                              disabled={this.state.saving}
                            >
                              {this.state.saving ? (
                                <span>
                                  <Icon type={'loading'} />
                                  &nbsp;
                                </span>
                              ) : undefined}
                              <Icon type={'save'} /> Routevolgorde opslaan
                            </Button>
                            <br />
                            <ul>
                              {routeTemplate.day_of_week === 2 && (
                                <li>
                                  <em style={{ color: '#aaa' }}>
                                    Pas de route volgorde <strong>voor zondag 22 uur</strong> aan.
                                  </em>
                                </li>
                              )}
                              {routeTemplate.day_of_week === 5 && (
                                <li>
                                  <em style={{ color: '#aaa' }}>
                                    Pas de route volgorde <strong>voor dinsdag 22 uur</strong> aan.
                                  </em>
                                </li>
                              )}
                            </ul>
                            <Spin spinning={this.state.saving}>
                              <SortableList
                                onSortEnd={({ oldIndex, newIndex }) =>
                                  this.onSortEnd(oldIndex, newIndex, templateIndex)
                                }
                                helperClass={'sorting'}
                                items={routeTemplate.route_customers}
                              />
                            </Spin>
                          </Col>
                          <Col xs={24} sm={12}>
                            <RouteMapAlt template={routeTemplate} height={900} width={100} />
                          </Col>
                        </Row>
                      </TabPane>
                    );
                  } else {
                    return undefined;
                  }
                })}
              </Tabs>
            </TabPane>
          )}
        </Tabs>
      </TitledContainer>
    );
  }
}

const mapStateToProps = (appState) => ({
  routes: appState.routes,
  auth: appState.auth,
  table: appState.table
});

const mapDispatchToProps = (dispatch) => ({
  fetchRouteTemplates: (driverId: number, filter: {}) => {
    dispatch(fetchRouteTemplates(driverId, filter));
  },
  fetchActualDepotRoutes: () => {
    dispatch(fetchActualDepotRoutes());
  },
  swapIndexes: (a: number, b: number, routeIndex: number) => {
    dispatch(swapIndexes(a, b, routeIndex));
  }
});

export default connect<any, any, any>(mapStateToProps, mapDispatchToProps)(Routes);
